import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { User } from '../../models/user';
import { OrderService } from '../../services/order.service';
import { SpinnerService } from '../../services/spinner.service';
import { UserService } from '../../services/user.service';
import { SnackbarService } from '../../services/snackbar.service';
import { ModalComponent } from '../modal/modal.component';
import { DateTime } from 'luxon';

@Component({
	selector: 'app-barcode_translation-modal',
	templateUrl: './barcode-translation-modal.html',
	styleUrls: ['./barcode-translation-modal.component.scss']
})
export class BarcodeTranslationModalComponent {
	public barcode: string;
	public placementOption: string = 'manual';
	public user: User;

	constructor(public dialogRef: MatDialogRef<BarcodeTranslationModalComponent>,
				@Inject(MAT_DIALOG_DATA) public data: any,
				private _dialog: MatDialog,
				private _orderService: OrderService,
				private _userService: UserService,
				private _snackbarService: SnackbarService,
				private _spinnerService: SpinnerService) {
	}

	ngOnInit() {
		this._userService.user$.subscribe((user) => {
			this.user = user;
		});
	}

	apply(): void {
		if (this.placementOption === 'manual' && this.data.orderIsDirty) {
			const dialogRef = this._dialog.open(ModalComponent, {
				data: {
					title: 'Warning',
					message: 'You are attempting to scan a new barcode while you still have an unplaced order on the page which will result in the order not being placed. Are you sure you would like to proceed?',
					showCancel: true,
					cancelText: 'Cancel',
					okText: 'Yes'
				}
			});
			dialogRef.afterClosed().subscribe((result) => {
				if (result) {
					this.createOrder();
				}
				else {
					return;
				}
			});
		}
		else {
			this.createOrder();
		}
	}

	clearBarcode(): void {
		this.barcode = '';
	}

	createOrder(): void {
		let placeAutomatically = this.placementOption === 'auto';
		this._spinnerService.startSpinner();
		this._orderService.createOrderFromBarcode(this.barcode, this.data.customer.id, this.data.barcodeTranslationConfig.id, placeAutomatically).subscribe({
			next: (response) => {
				this._spinnerService.stopSpinner();
				if (response.errors && response.errors.length > 0) {
					response.errors.forEach(error => {
						this._snackbarService.openSnackBar(error, 'Close', 10000);
					});
					this.placementOption = 'manual'; //proceed as manual
				}
				if (this.placementOption === 'manual') {
					this.dialogRef.close(response);
				}
				else if (this.placementOption === 'auto') {
					if (response.onDemandOrder.id) {
						this._snackbarService.openSnackBar(`Barcode scan successful. Order ${response.onDemandOrder.id} created.`, 'Close', 10000, 'success');
						this.clearBarcode();
					}
					else if (this.user?.allowCartCheckout) {
						this.addToCart(response.onDemandOrder);
						this._snackbarService.openSnackBar('Barcode scan failed. Please review the order in the shopping cart and resubmit it after corrections have been made.', 'Close', 10000);
						this.clearBarcode();
					}
					else {
						this._snackbarService.openSnackBar('Barcode scan failed. Please correct any issues with your data below to manually place your order.', 'Close', 10000);
						this.dialogRef.close(response);
					}
				}
				else if (this.placementOption === 'cart') {
					this.addToCart(response.onDemandOrder);
					this._snackbarService.openSnackBar('Order added to cart.', 'Close', 6000, 'success');
					this.clearBarcode();
				}
			}, error: (error) => {
				this._spinnerService.stopSpinner();
				this._snackbarService.openSnackBar(error.error.detailedMsg, 'Close', 6000);
			}
		});
	}

	addToCart(order): void {
		let cartOrder = {
			order: order,
			originAddress: order.origin,
			destAddress: order.destination,
			totalWeight: order.weight,
			totalPieces: order.pieces,
			pickupDate: order.readyTimeFrom?.standardizedDate,
			deliveryDate: this.user.inetUser.allowDueTimeFrom ? order.dueTimeFrom?.standardizedDate : null,
			pickupTime: order.readyTimeFrom?.standardizedTime,
			deliveryTime: this.user.inetUser.allowDueTimeFrom ? order.dueTimeFrom?.standardizedTime : null,
			validPickupDate: true,
			validDeliveryDate: true,
			availableOrderTypes: null,
			availableItems: null,
			readyTimeFrom: order.readyTimeFrom?.timeStamp,
			dueTimeFrom: this.user.inetUser.allowDueTimeFrom ? order.dueTimeFrom?.timeStamp : null
		};
    cartOrder.order.readyTimeFrom = DateTime.fromFormat(`${cartOrder.pickupDate} ${cartOrder.pickupTime}`, 'yyyy-LL-dd HH:mm').toUTC().valueOf();
    cartOrder.order.dueTimeFrom = this.user.inetUser.allowDueTimeFrom ? DateTime.fromFormat(`${cartOrder.deliveryDate} ${cartOrder.deliveryTime}`, 'yyyy-LL-dd HH:mm').toUTC().valueOf() : null;
		cartOrder.order.selectedPickupReady = 'later';
		cartOrder.order.selectedDeliveryReady = 'later';
		cartOrder.order.notificationEmails = order.emailPod ? order.emailPod?.split(';') : [];
		cartOrder.originAddress.contact = order.contact;
		cartOrder.destAddress.contact = order.attentionTo;

		const getOrderTypesData = {
			originAddress: cartOrder.originAddress,
			destinationAddress: cartOrder.destAddress,
			custId: cartOrder.order.custId,
			serviceType: cartOrder.order.serviceType,
			datePlaced: null,
			readyTimeFrom: cartOrder.order.readyTimeFrom,
			dueTimeFrom: cartOrder.order.dueTimeFrom,
			weight: cartOrder.totalWeight,
			pieces: cartOrder.totalPieces,
			parcels: cartOrder.order.parcels,
			userId: this.user.inetUser.id,
			parentOrderId: cartOrder?.order?.parentOrderId,
			orderId: null
		};
		this._orderService.getOrderTypes(getOrderTypesData).subscribe((response) => {
			if (response && response.length > 0) {
				cartOrder.availableOrderTypes = response;
				const selectedOT = response.find((orderType) => {
					return orderType.orderTypeSummary.orderTypeId === cartOrder.order.orderType;
				});
				if (selectedOT) {
					cartOrder.order.selectedOrderType = selectedOT;
					this._orderService.getAvailableItems(cartOrder.order.orderType, this.data.customer.itemRateChartId).subscribe((response) => {
						if (response && response.length > 0) {
							cartOrder.availableItems = response;
						}
						this._orderService.getTimeZoneOffset(cartOrder.originAddress.zip, cartOrder.pickupDate).subscribe((response: any) => {
							cartOrder.order.pickupTimeOffset = response.utcOffset ? response.utcOffset : new Date().getTimezoneOffset();
							cartOrder.order.pickupTimeZone = response.timezoneAbbrev;
							cartOrder.pickupTime = DateTime.fromFormat(cartOrder.pickupTime, 'HH:mm').toFormat('HH:mm');
							this._orderService.getTimeZoneOffset(cartOrder.destAddress.zip, cartOrder.deliveryDate).subscribe((response: any) => {
								cartOrder.order.deliveryTimeOffset = response.utcOffset ? response.utcOffset : new Date().getTimezoneOffset();
								cartOrder.order.deliveryTimeZone = response.timezoneAbbrev;
								if (cartOrder.deliveryTime) {
                  const t = DateTime.fromFormat(cartOrder.deliveryTime, 'HH:mm').toFormat('HH:mm');
									const difference = (cartOrder.order.pickupTimeOffset - cartOrder.order.deliveryTimeOffset) / -60;
									order.deliveryTime = this.adjustTime(t, difference);
								}
								const orderDTO = this._orderService.createDTOForOrder(cartOrder.order, false, cartOrder.order.selectedItems);
								this._orderService.getSurchargesAndTaxes(orderDTO).subscribe((response) => {
									this.processUpdateTotalsResponse(response, cartOrder, false);
									this.calculateOrderTotal(cartOrder.order);
								}, () => { });
							}, () => { });
						}, () => { });
					}, () => { });
					this._orderService.addOrderToQueue(cartOrder);
				} else {
					this._orderService.addOrderToQueue(cartOrder);
				}
			} else {
				this._orderService.addOrderToQueue(cartOrder);
			}
		}, () => {
			this._orderService.addOrderToQueue(cartOrder);;
		});
	}

	processUpdateTotalsResponse(response, order, loadingItem?) {
		let baseRate = null;
		let additionalCharges = 0;
		const itemizedCharges = [];
		response.displayCharges.forEach(item => {
			if (item.id !== 0) {
				additionalCharges += item.price;
				itemizedCharges.push(item);
			} else {
				baseRate = item;
			}
		});
		if (loadingItem) {
			response.itemRates.forEach(item => {
				if (item.id === loadingItem.id) {
					loadingItem.rate = item.price;
					loadingItem.loading = false;
				}
			});
		}
		itemizedCharges.unshift(baseRate);
		if (order.order.selectedOrderType) {
			order.order.selectedOrderType.itemizedCharges = itemizedCharges;
		}
		order.order.orderSubtotal = baseRate.price + additionalCharges;
		order.order.orderCharges = response.displayCharges;
	}

	calculateOrderTotal(order) {
		let total = 0;
		if (order.orderCharges) {
			for (let i = 0; i < order.orderCharges.length; i++) {
				if (order.orderCharges[i].price) {
					total = total + order.orderCharges[i].price;
				}
			}
		}
		order.orderTotal = total;
	}

	adjustTime(t, d) {
		const split = t.toString().split(':');
		const newHour = parseInt(split[0], 10) + d;
		return newHour + ':' + split[1];
	}
}
